<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref } from 'vue';

import { useLocales } from '@/composables/useLocales';
import { useUserLocale } from '@/composables/useUserLocale';
import { changeLanguage, locale as activeLocale } from '@/config/i18n';

const t = inject<(key: string) => string>('t')!;

// Liste des langues : static-data/locales.json du CDN, comme assessments et
// personal-account. Le CDN porte aussi la clé de traduction de chaque langue
// (`language_key`), donc plus de mapping ISO -> clé reconstruit à la main ici.
const { locales, toSupportedLocale } = useLocales();

function getLanguageName(languageKey: string) {
  return t(languageKey);
}

// Dérivé de la langue d'i18next, et pas lu une fois dans le cookie : le header
// se monte hors du RouterView, donc avant que le garde ait aligné l'app sur la
// langue du compte — un état figé au setup afficherait l'ancienne langue.
const currentLocale = computed(() => toSupportedLocale(activeLocale.value) ?? 'en-US');

const currentLanguageKey = computed(() => currentLocale.value.split('-')[0].toUpperCase());

const isOpen = ref(false);

const sortedLocales = computed(() =>
  [...locales.value].sort((a, b) => getLanguageName(a.language_key).localeCompare(getLanguageName(b.language_key)))
);

async function changeLocale(iso: string) {
  isOpen.value = false;
  await changeLanguage(iso);
  // Un utilisateur connecté choisit sa langue pour de bon : sans écriture sur le
  // compte, la synchro depuis /me la remettrait à l'ancienne au rechargement.
  await useUserLocale().persistLocaleForUser(iso);
}

const container = ref<HTMLElement>();
function onClickOutside(e: MouseEvent) {
  if (container.value && !container.value.contains(e.target as Node)) {
    isOpen.value = false;
  }
}
onMounted(() => document.addEventListener('click', onClickOutside));
onUnmounted(() => document.removeEventListener('click', onClickOutside));
</script>

<template>
  <div ref="container" class="relative" data-cy="navbar-language" @keydown.esc="isOpen = false">
    <button
      type="button"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      class="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-sm text-af-grey-600 transition-colors hover:bg-af-blue-100 hover:text-af-blue-600 disabled:pointer-events-none disabled:opacity-50"
      @click="isOpen = !isOpen"
    >
      <FaIcon :icon="['fas', 'earth-americas']" class="h-4 w-4 shrink-0" />
      {{ currentLanguageKey }}
    </button>

    <Transition
      enterActiveClass="transition ease-out duration-150"
      enterFromClass="opacity-0 translate-y-1"
      enterToClass="opacity-100 translate-y-0"
      leaveActiveClass="transition ease-in duration-100"
      leaveFromClass="opacity-100 translate-y-0"
      leaveToClass="opacity-0 translate-y-1"
    >
      <div
        v-if="isOpen"
        role="menu"
        class="absolute inset-e-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-sm border border-af-grey-200 bg-white shadow-md"
      >
        <div class="max-h-47.5 overflow-y-auto">
          <button
            v-for="locale in sortedLocales"
            :key="locale.id"
            type="button"
            role="menuitemradio"
            :aria-checked="locale.iso_code_639 === currentLocale"
            class="w-full px-4 py-2 text-start text-sm text-af-grey-600 transition-colors hover:bg-af-grey-100 focus-visible:outline-0! focus:bg-af-grey-100"
            :class="{ 'font-semibold text-af-grey-800': locale.iso_code_639 === currentLocale }"
            @click="changeLocale(locale.iso_code_639)"
          >
            {{ getLanguageName(locale.language_key) }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>
